<template>
  <button type="button" class="idle-cat" :aria-label="label" @click="onTap">
    <span ref="catBody" class="idle-cat__body">
      <img
        v-if="imageSrc"
        class="idle-cat__image"
        :src="imageSrc"
        alt=""
        draggable="false"
        @error="showFallback = true"
      />
      <span v-else class="idle-cat__fallback" aria-hidden="true">🐱</span>
    </span>
    <span v-for="note in notes" :key="note.id" :ref="`note-${note.id}`" class="idle-cat__note">
      {{ note.glyph }}
    </span>
  </button>
</template>

<script lang="ts">
import Vue from "vue";
import { assistantCatGroupFromType, assistantCatWholeBodyImageUrl } from "@/constants/assistantCatHome";
import { AssistantCatGroup } from "@/lib/models/assistantCat";

type Gesture = "bounce" | "sway" | "nod" | "stretch" | "hop";

interface NoteSpawn {
  id: number;
  left: number;
  top: number;
  driftX: number;
  spin: number;
  color: string;
  glyph: string;
  startedAt: number;
}

interface Xf {
  offsetY: number;
  angle: number;
  scaleX: number;
  scaleY: number;
}

const IDENTITY: Xf = { offsetY: 0, angle: 0, scaleX: 1, scaleY: 1 };
const IDLE_GESTURES: Gesture[] = ["bounce", "sway", "nod", "stretch"];
const MA_NOTE_COLORS = ["#FF6B6B", "#FF9F43", "#FFE66D", "#2ECC71", "#4ECDC4", "#54A0FF", "#A87FE0", "#FF8FAB"];
const LP_NOTE_GLYPHS = ["♪", "♫", "♩", "♬"];
const GESTURE_MS: Record<Gesture, number> = {
  bounce: 900,
  sway: 1400,
  nod: 1000,
  stretch: 1100,
  hop: 640,
};
const BREATH_MS = 3200;
const NOTE_MS = 900;
const TAP_ANALYTICS_DEBOUNCE_MS = 1500;

function easeInOut(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function compose(a: Xf, b: Xf): Xf {
  return {
    offsetY: a.offsetY + b.offsetY,
    angle: a.angle + b.angle,
    scaleX: a.scaleX * b.scaleX,
    scaleY: a.scaleY * b.scaleY,
  };
}

function breathAt(t: number): Xf {
  const eased = t < 0.55 ? (t / 0.55) * 0.5 : 0.5 + ((t - 0.55) / 0.45) * 0.5;
  const wave = Math.sin(eased * Math.PI * 2);
  return {
    offsetY: -2.2 * wave,
    angle: 0.008 * wave,
    scaleX: 1 - 0.006 * wave,
    scaleY: 1 + 0.012 * wave,
  };
}

function gestureAt(gesture: Gesture, rawT: number): Xf {
  const t = easeInOut(rawT);
  switch (gesture) {
    case "bounce":
      return { offsetY: -5 * Math.sin(t * Math.PI), angle: 0, scaleX: 1, scaleY: 1 };
    case "sway":
      return { offsetY: 0, angle: 0.03 * Math.sin(t * Math.PI * 2), scaleX: 1, scaleY: 1 };
    case "nod": {
      const wave = Math.sin(t * Math.PI);
      return { offsetY: 2 * wave, angle: 0.05 * wave, scaleX: 1, scaleY: 1 };
    }
    case "stretch": {
      const stretch = Math.sin(t * Math.PI);
      return { offsetY: -3 * stretch, angle: 0, scaleX: 1 - 0.02 * stretch, scaleY: 1 + 0.04 * stretch };
    }
    case "hop": {
      const up = Math.sin(t * Math.PI);
      const land = t > 0.55 ? ((t - 0.55) / 0.45) * (1 - t) * 4 : 0;
      const squash = up * Math.min(1, Math.max(0, land));
      return { offsetY: -14 * up, angle: 0, scaleX: 1 + 0.06 * squash, scaleY: 1 - 0.08 * squash };
    }
    default:
      return IDENTITY;
  }
}

export default Vue.extend({
  name: "AssistantCatHomeIdleCat",
  props: {
    writerType: {
      type: String,
      default: null,
    },
    catName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      showFallback: false,
      notes: [] as NoteSpawn[],
      nextNoteId: 0,
      animationsEnabled: true,
      alive: true,
      gesture: "bounce" as Gesture,
      gestureStartedAt: 0,
      gestureDuration: 0,
      gestureActive: false,
      breathStartedAt: 0,
      rafId: 0,
      idleTimer: null as number | null,
      tapAnalyticsTimer: null as number | null,
      pendingTapCount: 0,
      motionQuery: null as MediaQueryList | null,
    };
  },
  computed: {
    imageSrc(): string {
      if (this.showFallback) {
        return "";
      }
      return assistantCatWholeBodyImageUrl(this.writerType) || "";
    },
    label(): string {
      return this.catName || (this.$t("assistantCatHome.pageTitle") as string);
    },
    group(): AssistantCatGroup {
      return assistantCatGroupFromType(this.writerType);
    },
  },
  watch: {
    writerType() {
      this.showFallback = false;
    },
  },
  mounted() {
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.setAnimationsEnabled(!this.motionQuery.matches);
    this.motionQuery.addEventListener("change", this.onMotionPreferenceChange);
    document.addEventListener("visibilitychange", this.onPageHidden);
    window.addEventListener("pagehide", this.onPageHidden);
  },
  beforeDestroy() {
    this.alive = false;
    this.motionQuery?.removeEventListener("change", this.onMotionPreferenceChange);
    document.removeEventListener("visibilitychange", this.onPageHidden);
    window.removeEventListener("pagehide", this.onPageHidden);
    this.stopLoop();
    this.clearIdleTimer();
    this.flushTapAnalytics();
    if (this.tapAnalyticsTimer !== null) {
      window.clearTimeout(this.tapAnalyticsTimer);
      this.tapAnalyticsTimer = null;
    }
  },
  methods: {
    onMotionPreferenceChange() {
      this.setAnimationsEnabled(!this.motionQuery?.matches);
    },
    onPageHidden() {
      if (document.visibilityState === "hidden") {
        this.flushTapAnalytics();
      }
    },
    setAnimationsEnabled(enabled: boolean) {
      if (enabled === this.animationsEnabled && this.rafId) {
        return;
      }
      this.animationsEnabled = enabled;
      if (enabled) {
        this.breathStartedAt = performance.now();
        if (!this.rafId) {
          this.loop();
        }
        if (!this.gestureActive) {
          this.scheduleNextGesture();
        }
        return;
      }
      this.stopLoop();
      this.clearIdleTimer();
      this.gestureActive = false;
      this.notes = [];
      this.resetTransform();
    },
    loop() {
      this.rafId = window.requestAnimationFrame((now) => {
        if (!this.alive) {
          return;
        }
        this.applyTransform(now);
        this.applyNotes(now);
        if (this.gestureActive && now - this.gestureStartedAt >= this.gestureDuration) {
          this.gestureActive = false;
          this.scheduleNextGesture();
        }
        this.loop();
      });
    },
    stopLoop() {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }
    },
    resetTransform() {
      const body = this.$refs.catBody as HTMLElement | undefined;
      if (body) {
        body.style.transform = "";
      }
    },
    applyTransform(now: number) {
      const body = this.$refs.catBody as HTMLElement | undefined;
      if (!body) {
        return;
      }
      const breathT = ((now - this.breathStartedAt) % BREATH_MS) / BREATH_MS;
      const gestureT =
        this.gestureActive && this.gestureDuration > 0
          ? Math.min(1, (now - this.gestureStartedAt) / this.gestureDuration)
          : 0;
      const xf = compose(breathAt(breathT), this.gestureActive ? gestureAt(this.gesture, gestureT) : IDENTITY);
      body.style.transform = `translateY(${xf.offsetY}px) rotate(${xf.angle}rad) scale(${xf.scaleX}, ${xf.scaleY})`;
    },
    applyNotes(now: number) {
      if (this.notes.length === 0) {
        return;
      }
      const doneIds: number[] = [];
      this.notes.forEach((note) => {
        const raw = Math.min(1, (now - note.startedAt) / NOTE_MS);
        const el = this.noteElement(note.id);
        if (raw >= 1) {
          doneIds.push(note.id);
          return;
        }
        if (!el) {
          return;
        }
        const t = easeOutCubic(raw);
        const opacity = Math.min(1, Math.max(0, 1 - raw * raw));
        const scale = 0.85 + 0.25 * Math.sin(raw * Math.PI);
        el.style.left = `${note.left - 14 + note.driftX * t}px`;
        el.style.top = `${note.top - 80 * t}px`;
        el.style.color = note.color;
        el.style.opacity = String(opacity);
        el.style.transform = `rotate(${note.spin * t}rad) scale(${scale})`;
      });
      if (doneIds.length > 0) {
        this.notes = this.notes.filter((note) => !doneIds.includes(note.id));
      }
    },
    noteElement(id: number): HTMLElement | undefined {
      const ref = this.$refs[`note-${id}`];
      if (Array.isArray(ref)) {
        return ref[0] as HTMLElement | undefined;
      }
      return ref as HTMLElement | undefined;
    },
    clearIdleTimer() {
      if (this.idleTimer !== null) {
        window.clearTimeout(this.idleTimer);
        this.idleTimer = null;
      }
    },
    scheduleNextGesture() {
      this.clearIdleTimer();
      if (!this.animationsEnabled) {
        return;
      }
      const wait = 4000 + Math.floor(Math.random() * 4001);
      this.idleTimer = window.setTimeout(() => this.playRandomGesture(), wait);
    },
    playRandomGesture() {
      const next = IDLE_GESTURES[Math.floor(Math.random() * IDLE_GESTURES.length)];
      this.playGesture(next);
    },
    playGesture(gesture: Gesture) {
      if (!this.animationsEnabled) {
        this.scheduleNextGesture();
        return;
      }
      this.clearIdleTimer();
      this.gesture = gesture;
      this.gestureDuration = GESTURE_MS[gesture];
      this.gestureStartedAt = performance.now();
      this.gestureActive = true;
    },
    onTap() {
      this.registerTapForAnalytics();
      if (typeof navigator.vibrate === "function") {
        navigator.vibrate(10);
      }
      if (!this.animationsEnabled) {
        return;
      }
      this.spawnNote();
      this.playGesture("hop");
    },
    registerTapForAnalytics() {
      this.pendingTapCount += 1;
      if (this.tapAnalyticsTimer !== null) {
        window.clearTimeout(this.tapAnalyticsTimer);
      }
      this.tapAnalyticsTimer = window.setTimeout(() => this.flushTapAnalytics(), TAP_ANALYTICS_DEBOUNCE_MS);
    },
    flushTapAnalytics() {
      const count = this.pendingTapCount;
      this.pendingTapCount = 0;
      if (this.tapAnalyticsTimer !== null) {
        window.clearTimeout(this.tapAnalyticsTimer);
        this.tapAnalyticsTimer = null;
      }
      if (count <= 0) {
        return;
      }
      this.$emit("tap-burst", count);
    },
    noteGlyph(): string {
      if (this.group === "MP") {
        return "♫";
      }
      if (this.group === "LP") {
        return LP_NOTE_GLYPHS[Math.floor(Math.random() * LP_NOTE_GLYPHS.length)];
      }
      return "♪";
    },
    spawnNote() {
      const width = (this.$el as HTMLElement).clientWidth || 1;
      const height = (this.$el as HTMLElement).clientHeight || width * (334 / 301);
      const color = this.group === "MA" ? MA_NOTE_COLORS[Math.floor(Math.random() * MA_NOTE_COLORS.length)] : "#ffffff";
      const glyph = this.noteGlyph();
      this.notes.push({
        id: this.nextNoteId,
        left: width * (0.68 + Math.random() * 0.16),
        top: height * (0.12 + Math.random() * 0.1),
        driftX: 8 + Math.random() * 28,
        spin: (Math.random() - 0.5) * 0.5,
        color,
        glyph,
        startedAt: performance.now(),
      });
      this.nextNoteId += 1;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.idle-cat {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  overflow: visible;
  border: none;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  user-select: none;
  appearance: none;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--color-focus-ring);
    border-radius: var(--radius-sm);
  }

  &:active {
    background: transparent;
  }
}

.idle-cat__body {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: center bottom;
  pointer-events: none;
}

.idle-cat__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  pointer-events: none;
}

.idle-cat__fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 48px;
}

.idle-cat__note {
  position: absolute;
  z-index: 2;
  font-size: 28px;
  line-height: 1;
  text-shadow: 0 1px 4px #00000066;
  pointer-events: none;
  will-change: transform, opacity;
}
</style>
